import { useCallback, useEffect, useState } from 'react';
import { logger } from '@/lib/logger';

// Standardized localStorage key prefixes
export const STORAGE_PREFIXES = {
  APP: 'app-',
  USER: 'app-user-',
  PROJECT: 'app-project-',
  SETTINGS: 'app-settings-',
  CACHE: 'app-cache-',
  TEMP: 'app-temp-'
} as const;

// Deprecated keys that should be cleaned up
export const DEPRECATED_KEYS = [
  'sheetsync-sheet-id',
  'column-mapping',
  'mapping-conflicts', 
  'sheetsync-settings',
  'sync-preview-data',
  // Add old specs keys without proper SPECS_ prefix
  'specs-pin-mode',
  'specs-view-mode', 
  'specs-group-by-layer',
  'specs-selected-layer-id',
  'specs-search-term',
  'specs-status-filter'
] as const;

// Deprecated key prefixes
// NOTE: 'spec-' and 'specs-' removed - they are used for valid draft/preferences
export const DEPRECATED_PREFIXES = [
  'sheetsync-',
  'column-mapping-',
  'mapping-'
] as const;

interface StorageMetrics {
  totalKeys: number;
  appKeys: number;
  deprecatedKeys: number;
  totalSize: number;
  lastCleanup: number;
}

interface StorageValidation {
  isValid: boolean;
  issues: string[];
  metrics: StorageMetrics;
}

export function useLocalStorageManager() {
  const [metrics, setMetrics] = useState<StorageMetrics>({
    totalKeys: 0,
    appKeys: 0,
    deprecatedKeys: 0,
    totalSize: 0,
    lastCleanup: 0
  });

  // Get standardized key with prefix
  const getStandardKey = useCallback((key: string, prefix: keyof typeof STORAGE_PREFIXES = 'APP'): string => {
    const prefixValue = STORAGE_PREFIXES[prefix];
    return key.startsWith(prefixValue) ? key : `${prefixValue}${key}`;
  }, []);

  // Clean up deprecated keys
  const cleanupDeprecatedKeys = useCallback(() => {
    let cleanedCount = 0;
    
    try {
      // Remove specific deprecated keys
      DEPRECATED_KEYS.forEach(key => {
        if (localStorage.getItem(key) !== null) {
          localStorage.removeItem(key);
          cleanedCount++;
          logger.debug(`🗑️ [Storage] Removed deprecated key: ${key}`);
        }
      });

      // Remove keys with deprecated prefixes
      Object.keys(localStorage).forEach(key => {
        DEPRECATED_PREFIXES.forEach(prefix => {
          if (key.startsWith(prefix)) {
            localStorage.removeItem(key);
            cleanedCount++;
            logger.debug(`🗑️ [Storage] Removed deprecated prefixed key: ${key}`);
          }
        });
      });

      if (cleanedCount > 0) {
        logger.info(`✅ [Storage] Cleaned up ${cleanedCount} deprecated keys`);
      }

      // Update last cleanup timestamp
      const cleanupKey = getStandardKey('last-cleanup', 'SETTINGS');
      localStorage.setItem(cleanupKey, Date.now().toString());

    } catch (error) {
      logger.error('[Storage] Error during cleanup:', error);
    }

    return cleanedCount;
  }, [getStandardKey]);

  // Calculate storage metrics
  const calculateMetrics = useCallback((): StorageMetrics => {
    try {
      const allKeys = Object.keys(localStorage);
      let totalSize = 0;
      let appKeys = 0;
      let deprecatedKeys = 0;

      allKeys.forEach(key => {
        const value = localStorage.getItem(key) || '';
        totalSize += key.length + value.length;

        // Count app keys
        if (Object.values(STORAGE_PREFIXES).some(prefix => key.startsWith(prefix))) {
          appKeys++;
        }

        // Count deprecated keys
        if (DEPRECATED_KEYS.includes(key as any) || 
            DEPRECATED_PREFIXES.some(prefix => key.startsWith(prefix))) {
          deprecatedKeys++;
        }
      });

      const lastCleanupKey = getStandardKey('last-cleanup', 'SETTINGS');
      const lastCleanup = parseInt(localStorage.getItem(lastCleanupKey) || '0');

      return {
        totalKeys: allKeys.length,
        appKeys,
        deprecatedKeys,
        totalSize,
        lastCleanup
      };
    } catch (error) {
      logger.error('[Storage] Error calculating metrics:', error);
      return {
        totalKeys: 0,
        appKeys: 0,
        deprecatedKeys: 0,
        totalSize: 0,
        lastCleanup: 0
      };
    }
  }, [getStandardKey]);

  // Validate storage consistency
  const validateStorage = useCallback((): StorageValidation => {
    const currentMetrics = calculateMetrics();
    const issues: string[] = [];

    // Check for deprecated keys
    if (currentMetrics.deprecatedKeys > 0) {
      issues.push(`Found ${currentMetrics.deprecatedKeys} deprecated keys that should be cleaned up`);
    }

    // Check storage size (warn if > 5MB)
    const sizeMB = currentMetrics.totalSize / (1024 * 1024);
    if (sizeMB > 5) {
      issues.push(`localStorage size is ${sizeMB.toFixed(2)}MB, consider cleanup`);
    }

    // Check if cleanup hasn't been run recently (7 days)
    const daysSinceCleanup = (Date.now() - currentMetrics.lastCleanup) / (1000 * 60 * 60 * 24);
    if (daysSinceCleanup > 7) {
      issues.push(`Last cleanup was ${Math.floor(daysSinceCleanup)} days ago`);
    }

    return {
      isValid: issues.length === 0,
      issues,
      metrics: currentMetrics
    };
  }, [calculateMetrics]);

  // Set value with standardized key
  const setStandardValue = useCallback(<T,>(
    key: string, 
    value: T, 
    prefix: keyof typeof STORAGE_PREFIXES = 'APP'
  ) => {
    try {
      const standardKey = getStandardKey(key, prefix);
      const serialized = JSON.stringify(value);
      localStorage.setItem(standardKey, serialized);
      logger.debug(`💾 [Storage] Set standardized key: ${standardKey}`);
      return true;
    } catch (error) {
      logger.error(`[Storage] Error setting ${key}:`, error);
      return false;
    }
  }, [getStandardKey]);

  // Get value with standardized key
  const getStandardValue = useCallback(<T,>(
    key: string, 
    defaultValue: T, 
    prefix: keyof typeof STORAGE_PREFIXES = 'APP'
  ): T => {
    try {
      const standardKey = getStandardKey(key, prefix);
      const item = localStorage.getItem(standardKey);
      if (!item) return defaultValue;
      
      const parsed = JSON.parse(item);
      return parsed !== null && parsed !== undefined ? parsed : defaultValue;
    } catch (error) {
      logger.error(`[Storage] Error getting ${key}:`, error);
      return defaultValue;
    }
  }, [getStandardKey]);

  // Remove value with standardized key
  const removeStandardValue = useCallback((
    key: string, 
    prefix: keyof typeof STORAGE_PREFIXES = 'APP'
  ) => {
    try {
      const standardKey = getStandardKey(key, prefix);
      localStorage.removeItem(standardKey);
      logger.debug(`🗑️ [Storage] Removed standardized key: ${standardKey}`);
      return true;
    } catch (error) {
      logger.error(`[Storage] Error removing ${key}:`, error);
      return false;
    }
  }, [getStandardKey]);

  // Auto-cleanup on component mount
  useEffect(() => {
    const validation = validateStorage();
    setMetrics(validation.metrics);

    // Auto-cleanup if needed
    if (validation.metrics.deprecatedKeys > 0) {
      cleanupDeprecatedKeys();
    }
  }, [cleanupDeprecatedKeys, validateStorage]);

  return {
    // Standardized CRUD operations
    setStandardValue,
    getStandardValue,
    removeStandardValue,
    getStandardKey,
    
    // Cleanup and maintenance
    cleanupDeprecatedKeys,
    validateStorage,
    calculateMetrics,
    
    // Metrics and monitoring
    metrics,
    
    // Constants for reference
    STORAGE_PREFIXES,
    DEPRECATED_KEYS,
    DEPRECATED_PREFIXES
  };
}